import { type PathSegment, type PathSegments } from "./esim-lookup.ordering.common";

export const MAX_RELAXED_ARRAY_DEPTH = 12;
// For N array-index segments, non-empty wildcard combinations = 2^N - 1.
export const MAX_RELAXED_PATH_CANDIDATES = 2 ** MAX_RELAXED_ARRAY_DEPTH - 1;
export const DEFAULT_MAX_RELAXED_PATH_CACHE_ENTRIES = 10_000;
const ARRAY_INDEX_WILDCARD_TOKEN = "i:*";

export function resolveMaxRelaxedPathCacheEntries(maxRelaxedPathCacheEntries: number | undefined): number {
  if (typeof maxRelaxedPathCacheEntries !== "number" || !Number.isFinite(maxRelaxedPathCacheEntries) || maxRelaxedPathCacheEntries < 1) {
    return DEFAULT_MAX_RELAXED_PATH_CACHE_ENTRIES;
  }

  return Math.floor(maxRelaxedPathCacheEntries);
}

// Encapsulates canonical path encoding and relaxed wildcard-candidate generation.
export class RelaxedPathResolver {
  private readonly candidatesByPath = new Map<string, string[]>();

  public constructor(private readonly maxCachedPaths: number = DEFAULT_MAX_RELAXED_PATH_CACHE_ENTRIES) {}

  // Joins path segments into canonical map-key format.
  public toPath(pathSegments: PathSegments): string {
    if (!pathSegments.length) {
      return "";
    }

    return `/${pathSegments.map((pathSegment) => this.toPathSegmentToken(pathSegment)).join("/")}`;
  }

  // Builds wildcard candidates for array-index path segments.
  // Candidate order is:
  // 1) fewer wildcards first (more specific paths first),
  // 2) within same wildcard count, left-most (outer) indexes are wildcarded first,
  //    which keeps deeper (inner) indexes fixed longer.
  public getRelaxedPathCandidates(pathSegments: PathSegments): readonly string[] {
    const path = this.toPath(pathSegments);
    const cachedCandidates = this.candidatesByPath.get(path);
    if (cachedCandidates !== undefined) {
      return cachedCandidates;
    }

    const arrayIndexPathIndexes = this.getArrayIndexPathIndexes(pathSegments);
    if (!arrayIndexPathIndexes.length) {
      const noCandidates: string[] = [];
      this.tryCacheCandidates(path, noCandidates);
      return noCandidates;
    }

    if (arrayIndexPathIndexes.length > MAX_RELAXED_ARRAY_DEPTH) {
      const fullyWildcardedCandidate = this.toPathWithWildcardedArrayIndexes(pathSegments, arrayIndexPathIndexes);
      const fullyWildcardedCandidates = [fullyWildcardedCandidate];
      this.tryCacheCandidates(path, fullyWildcardedCandidates);
      return fullyWildcardedCandidates;
    }

    const wildcardCombinations: number[][] = [];
    for (let wildcardCount = 1; wildcardCount <= arrayIndexPathIndexes.length; wildcardCount += 1) {
      this.collectWildcardIndexCombinations(
        arrayIndexPathIndexes.length - 1,
        wildcardCount,
        wildcardCombinations,
        MAX_RELAXED_PATH_CANDIDATES
      );
      if (wildcardCombinations.length >= MAX_RELAXED_PATH_CANDIDATES) {
        break;
      }
    }

    const candidates = wildcardCombinations.map((wildcardCombination) =>
      this.toWildcardedPathByCombination(pathSegments, arrayIndexPathIndexes, wildcardCombination)
    );

    this.tryCacheCandidates(path, candidates);
    return candidates;
  }

  // Builds a path by wildcarding only array-index segment positions in the supplied combination.
  private toWildcardedPathByCombination(
    pathSegments: PathSegments,
    arrayIndexPathIndexes: number[],
    wildcardCombination: number[]
  ): string {
    const wildcardedArrayIndexPathIndexes = wildcardCombination.map(
      (wildcardCombinationIndex) => arrayIndexPathIndexes[wildcardCombinationIndex]
    );

    return this.toPathWithWildcardedArrayIndexes(pathSegments, wildcardedArrayIndexPathIndexes);
  }

  // Builds a path and applies wildcard token to selected array-index segment positions.
  private toPathWithWildcardedArrayIndexes(pathSegments: PathSegments, wildcardedArrayIndexPathIndexes: number[]): string {
    if (!pathSegments.length) {
      return "";
    }

    const wildcardedArrayIndexPathIndexSet = new Set<number>(wildcardedArrayIndexPathIndexes);
    const pathTokens: string[] = [];
    for (let pathSegmentIndex = 0; pathSegmentIndex < pathSegments.length; pathSegmentIndex += 1) {
      const pathSegment = pathSegments[pathSegmentIndex];

      if (pathSegment.kind === "array-index" && wildcardedArrayIndexPathIndexSet.has(pathSegmentIndex)) {
        pathTokens.push(ARRAY_INDEX_WILDCARD_TOKEN);
        continue;
      }

      pathTokens.push(this.toPathSegmentToken(pathSegment));
    }

    return `/${pathTokens.join("/")}`;
  }

  private getArrayIndexPathIndexes(pathSegments: PathSegments): number[] {
    const arrayIndexPathIndexes: number[] = [];

    pathSegments.forEach((segment, index) => {
      if (segment.kind === "array-index") {
        arrayIndexPathIndexes.push(index);
      }
    });

    return arrayIndexPathIndexes;
  }

  // Collects combinations of array-index positions to wildcard.
  private collectWildcardIndexCombinations(
    maxIndexInclusive: number,
    wildcardCount: number,
    combinations: number[][],
    maxCandidateCount: number
  ): void {
    const positions = maxIndexInclusive + 1;
    if (wildcardCount < 1 || wildcardCount > positions) {
      return;
    }

    const combination = Array.from({ length: wildcardCount }, (_, index) => index);

    while (combinations.length < maxCandidateCount) {
      combinations.push([...combination]);

      let combinationIndex = wildcardCount - 1;
      while (combinationIndex >= 0 && combination[combinationIndex] === positions - wildcardCount + combinationIndex) {
        combinationIndex -= 1;
      }

      if (combinationIndex < 0) {
        break;
      }

      combination[combinationIndex] += 1;
      for (let nextIndex = combinationIndex + 1; nextIndex < wildcardCount; nextIndex += 1) {
        combination[nextIndex] = combination[nextIndex - 1] + 1;
      }
    }
  }

  // Encodes path segments into disjoint tokens so object keys never collide with array-index segments.
  private toPathSegmentToken(pathSegment: PathSegment): string {
    if (pathSegment.kind === "array-index") {
      return `i:${pathSegment.value}`;
    }

    return `k:${encodeURIComponent(pathSegment.value)}`;
  }

  private tryCacheCandidates(path: string, candidates: string[]): void {
    // Bound cache growth: once full, skip caching new entries instead of growing indefinitely.
    if (this.candidatesByPath.size >= this.maxCachedPaths) {
      return;
    }

    this.candidatesByPath.set(path, candidates);
  }
}
